// src/components/BannedScreen.js - Completely fixed version
import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Timestamp,
  collection,
  query,
  where,
  getDocs,
} from "firebase/firestore";
import { useUser } from "../contexts/UserContext";
import notificationService from "../services/notificationService";
import { db } from "../firebase/config";

/**
 * Component that shows when a user is banned and provides information about why
 */
const BannedScreen = ({ providedBanInfo = null }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isBanned, banInfo: contextBanInfo, signOut } = useUser();
  const [countdown, setCountdown] = useState(300); // Default to 5 minutes
  const [contactSubmitted, setContactSubmitted] = useState(false);
  const [existingAppeal, setExistingAppeal] = useState(null);
  const [contactForm, setContactForm] = useState({
    message: "",
    contactInfo: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [loading, setLoading] = useState(true);

  // Use provided ban info or context ban info
  const banInfo = providedBanInfo ||
    contextBanInfo || {
      reason: "Violation of platform terms",
      timestamp: new Date(),
      banType: "Account",
    };

  // Force the component to persist when user is banned
  useEffect(() => {
    // If on the banned page, mark the user as banned in session storage
    if (location.pathname === "/banned" && (isBanned || providedBanInfo)) {
      sessionStorage.setItem("userIsBanned", "true");
    }
  }, [location.pathname, isBanned, providedBanInfo]);

  // Redirect away if not banned or if not on appeal page
  useEffect(() => {
    if (
      !isBanned &&
      !providedBanInfo &&
      !sessionStorage.getItem("userIsBanned")
    ) {
      navigate("/");
    }
  }, [isBanned, providedBanInfo, navigate]);

  // Load appeal from storage at component mount
  useEffect(() => {
    // Check if we have a submitted appeal in storage
    const storedAppeal = localStorage.getItem("bannedUserAppeal");
    if (storedAppeal) {
      try {
        const parsedAppeal = JSON.parse(storedAppeal);
        setExistingAppeal(parsedAppeal);
        setContactSubmitted(true);
      } catch (e) {
        console.error("Error parsing stored appeal:", e);
      }
    }
  }, []);

  // Check for existing appeals when component mounts
  useEffect(() => {
    const checkExistingAppeals = async () => {
      if (!user?.uid) {
        setLoading(false);
        return;
      }

      try {
        // Try to find appeals in all collections
        const collections = ["banAppeals", "appealRequests", "appeals"];
        let foundAppeals = [];

        for (const collectionName of collections) {
          try {
            const appealsQuery = query(
              collection(db, collectionName),
              where("userId", "==", user.uid)
            );
            const appealsSnapshot = await getDocs(appealsQuery);

            appealsSnapshot.forEach((doc) => {
              foundAppeals.push({ id: doc.id, ...doc.data() });
            });
          } catch (err) {
            console.log(`Could not check ${collectionName}:`, err);
            // Continue checking other collections
          }
        }

        // If any appeals found, use the most recent one
        if (foundAppeals.length > 0) {
          // Sort by most recent
          foundAppeals.sort((a, b) => {
            const dateA = a.createdAt?.toDate?.() || a.timestamp || new Date(0);
            const dateB = b.createdAt?.toDate?.() || b.timestamp || new Date(0);
            return dateB - dateA;
          });

          if (foundAppeals.length > 0) {
            const mostRecentAppeal = foundAppeals[0];
            setExistingAppeal(mostRecentAppeal);
            setContactSubmitted(true);

            // Also store this appeal in localStorage
            localStorage.setItem(
              "bannedUserAppeal",
              JSON.stringify(mostRecentAppeal)
            );
          }
        }
      } catch (error) {
        console.error("Error checking for existing appeals:", error);
      } finally {
        setLoading(false);
      }
    };

    checkExistingAppeals();
  }, [user]);

  // Setup the countdown timer
  useEffect(() => {
    // Get or initialize the ban end time
    const setupTimer = () => {
      // Try to get a stored end time
      const storedEndTimeStr = localStorage.getItem("banScreenEndTime");

      let endTime;
      if (storedEndTimeStr) {
        const storedEndTime = parseInt(storedEndTimeStr, 10);
        const now = Date.now();

        // If the stored end time is in the future and valid
        if (storedEndTime && !isNaN(storedEndTime) && storedEndTime > now) {
          endTime = storedEndTime;
        } else {
          // Create a new 5-minute timer
          endTime = Date.now() + 300 * 1000; // 5 minutes
          localStorage.setItem("banScreenEndTime", endTime.toString());
        }
      } else {
        // No stored end time, create a new one
        endTime = Date.now() + 300 * 1000; // 5 minutes
        localStorage.setItem("banScreenEndTime", endTime.toString());
      }

      // Mark user as banned in session
      sessionStorage.setItem("userIsBanned", "true");

      // Update timer function
      const updateTimer = () => {
        const now = Date.now();
        const remaining = Math.max(0, Math.floor((endTime - now) / 1000));
        setCountdown(remaining);

        // If timer expires, clean up and sign out
        if (remaining <= 0) {
          clearInterval(timerId);
          localStorage.removeItem("banScreenEndTime");
          localStorage.removeItem("bannedUserAppeal");
          sessionStorage.removeItem("userIsBanned");

          const handleSignOut = async () => {
            try {
              await signOut();
              navigate("/");
            } catch (error) {
              console.error("Error signing out:", error);
              window.location.href = "/";
            }
          };

          handleSignOut();
        }
      };

      // Run once immediately
      updateTimer();

      // Then setup interval
      const timerId = setInterval(updateTimer, 1000);

      // Cleanup on unmount
      return () => clearInterval(timerId);
    };

    // Start the timer
    return setupTimer();
  }, [navigate, signOut]);

  // Format date helper
  const formatDate = (timestamp) => {
    if (!timestamp) return "Unknown";

    let date;
    if (timestamp.toDate) {
      // Handle Firestore timestamp
      date = timestamp.toDate();
    } else if (timestamp instanceof Date) {
      // Handle JavaScript Date
      date = timestamp;
    } else if (typeof timestamp === "object" && timestamp.seconds) {
      // Handle Firestore timestamp object format
      date = new Timestamp(timestamp.seconds, timestamp.nanoseconds).toDate();
    } else {
      // Handle unknown format
      return "Unknown";
    }

    return date.toLocaleDateString() + " at " + date.toLocaleTimeString();
  };

  // Handle contact form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setSubmitError(null);

    try {
      // Get the current user's ID if available
      const userId = user?.uid;

      // Create a support notification/ticket
      const supportRequest = {
        type: "system",
        title: "Ban Appeal Request",
        message: contactForm.message,
        contactInfo: contactForm.contactInfo,
        timestamp: new Date(),
        status: "pending",
        userId: userId || null, // Include the user ID if available
        userEmail: user?.email || null,
        banInfo: banInfo, // Include the ban info for context
      };

      // Use the admin notification method
      const result = await notificationService.createAdminNotification(
        supportRequest
      );

      // Store the sent appeal in localStorage so it persists across refreshes
      const appealData = {
        ...supportRequest,
        id: result.id,
      };
      localStorage.setItem("bannedUserAppeal", JSON.stringify(appealData));

      // Show success UI
      setContactSubmitted(true);
      setExistingAppeal(appealData);

      // Reset form
      setContactForm({
        message: "",
        contactInfo: "",
      });
    } catch (error) {
      console.error("Error submitting appeal:", error);
      setSubmitError("Failed to submit your appeal. Please try again later.");
    } finally {
      setSubmitting(false);
    }
  };

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setContactForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="min-h-screen bg-red-50 flex flex-col items-center justify-center p-4"
    >
      <div className="max-w-md w-full bg-white rounded-xl shadow-lg overflow-hidden">
        <div className="bg-red-600 py-6 text-center">
          <div className="flex justify-center mb-2">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-16 w-16 text-white"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              />
            </svg>
          </div>
          <h1 className="text-2xl font-bold text-white">Account Suspended</h1>
        </div>

        <div className="p-6">
          <div className="mb-6">
            <p className="text-gray-700 mb-4">
              Your account has been suspended and you no longer have access to
              this platform.
            </p>

            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-4">
              <h3 className="font-medium text-gray-900 mb-2">
                Suspension Details
              </h3>
              <p className="text-sm text-gray-600 mb-1">
                <span className="font-medium">Reason:</span>{" "}
                {banInfo?.reason || "Violation of platform terms"}
              </p>
              <p className="text-sm text-gray-600 mb-1">
                <span className="font-medium">Date:</span>{" "}
                {banInfo?.timestamp ? formatDate(banInfo.timestamp) : "Unknown"}
              </p>
              {/* <p className="text-sm text-gray-600">
                <span className="font-medium">Type:</span>{" "}
                {banInfo?.banType ? banInfo.banType.toUpperCase() : "Account"}
              </p> */}
            </div>
          </div>

          {loading ? (
            <div className="text-center py-4">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mx-auto"></div>
              <p className="mt-2 text-gray-600">
                Loading appeal information...
              </p>
            </div>
          ) : !contactSubmitted ? (
            <div className="border-t border-gray-200 pt-4">
              <h3 className="text-lg font-medium text-gray-900 mb-3">
                Request Appeal
              </h3>
              <form onSubmit={handleSubmit}>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Why should your account be reinstated?
                  </label>
                  <textarea
                    name="message"
                    rows="3"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    value={contactForm.message}
                    onChange={handleInputChange}
                    required
                    placeholder="Please provide details about why you believe this ban should be reviewed..."
                    disabled={submitting}
                  ></textarea>
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Contact information (email or phone)
                  </label>
                  <input
                    type="text"
                    name="contactInfo"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    value={contactForm.contactInfo}
                    onChange={handleInputChange}
                    required
                    placeholder="Where can we reach you?"
                    disabled={submitting}
                  />
                </div>

                {submitError && (
                  <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                    {submitError}
                  </div>
                )}

                <button
                  type="submit"
                  className="w-full bg-indigo-600 text-white py-2 px-4 rounded-lg hover:bg-indigo-700 transition-colors disabled:opacity-50"
                  disabled={submitting}
                >
                  {submitting ? (
                    <span className="flex items-center justify-center">
                      <svg
                        className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        ></circle>
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        ></path>
                      </svg>
                      Submitting...
                    </span>
                  ) : (
                    "Submit Appeal"
                  )}
                </button>
              </form>
            </div>
          ) : (
            <div className="border-t border-gray-200 pt-4">
              <div className="py-4">
                <div className="text-green-500 mb-2 text-center">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-12 w-12 mx-auto"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                </div>
                <h3 className="text-lg font-medium text-gray-900 mb-2 text-center">
                  Appeal Submitted
                </h3>
                <p className="text-gray-600 mb-4 text-center">
                  {/* Thank you for your submission. Our team will review your
                  request and contact you if your account is reinstated. */}
                  Our team will review your request.
                </p>

                {existingAppeal && (
                  <div className="bg-gray-50 rounded-lg p-4 mt-4">
                    <h4 className="font-medium text-gray-900 mb-2">
                      Your Appeal
                    </h4>
                    <div className="text-sm text-gray-600">
                      <p className="mb-2">
                        <span className="font-medium">Status:</span>{" "}
                        <span
                          className={`capitalize px-2 py-0.5 rounded-full text-xs ${
                            existingAppeal.status === "approved"
                              ? "bg-green-100 text-green-800"
                              : existingAppeal.status === "rejected"
                              ? "bg-red-100 text-red-800"
                              : "bg-yellow-100 text-yellow-800"
                          }`}
                        >
                          {existingAppeal.status || "pending"}
                        </span>
                      </p>
                      <p className="mb-2">
                        <span className="font-medium">Submitted on:</span>{" "}
                        {existingAppeal.timestamp
                          ? existingAppeal.timestamp instanceof Date
                            ? existingAppeal.timestamp.toLocaleString()
                            : formatDate(existingAppeal.timestamp)
                          : "Recently"}
                      </p>
                      <p className="mb-2">
                        <span className="font-medium">Your message:</span>
                      </p>
                      <div className="bg-white p-3 rounded border border-gray-200 mb-2">
                        {existingAppeal.message}
                      </div>
                      <p className="mb-2">
                        <span className="font-medium">Contact info:</span>{" "}
                        {existingAppeal.contactInfo}
                      </p>

                      {existingAppeal.notes && (
                        <>
                          <p className="mt-4 font-medium text-gray-900">
                            Admin Response:
                          </p>
                          <div className="bg-white p-3 rounded border border-gray-200 mt-1">
                            {existingAppeal.notes}
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          <div className="mt-6">
            <div className="text-center text-sm text-gray-500 mb-2">
              <p>
                You will be signed out in{" "}
                <span className="font-medium">
                  {Math.floor(countdown / 60)}:
                  {(countdown % 60).toString().padStart(2, "0")}
                </span>
              </p>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2.5">
              <div
                className="bg-brandAlt h-2.5 rounded-full transition-all duration-1000"
                style={{ width: `${(countdown / 300) * 100}%` }}
              ></div>
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default BannedScreen;
